import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, FileText, Tag } from 'lucide-react';
import React from 'react';
import ImageWithPlaceholder from '../../../shared/components/ImageWithPlaceholderProps';

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: '#000000F2',
  cardBorder: '#DDB88919',
  cardBorderSelected: '#DDB889',
  cardShadow: '#DDB88919',
  cardShadowSelected: '#DDB88933',
  titleText: '#DDB889',
  subtitleText: '#9CA3AF',
  infoText: '#DDB889',
  iconColor: '#DDB889',
  selectionBackground: '#DDB889',
  selectionIcon: '#000000',
  badgeBackground: '#000000F2',
  badgeBorder: '#DDB88919',
  buttonBackground: '#DDB889',
  buttonText: '#000000',
  placeholderGradientFrom: '#DDB88933',
  placeholderGradientTo: '#DDB8890D',
  placeholderIconBackground: '#DDB88933'
};

// Define the Category interface
export interface Category {
  id: string;
  name: string;
  description: string;
  image: {
    secure_url: string;
  };
}

interface CategoryCardProps {
  category: Category;
  isSelected: boolean;
  onToggle: (category: Category) => void;
  shouldShowReadMore: (text: string) => boolean;
  onReadMore: (id: string) => void;
  colors?: Partial<typeof defaultColors>;
  showDescriptions?: boolean;
  showImages?: boolean;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({
  category,
  isSelected,
  onToggle,
  shouldShowReadMore,
  onReadMore,
  colors = {},
  showDescriptions = true,
  showImages = true
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  // Get shadow style based on selection state
  const getShadowStyle = () => {
    return isSelected
      ? `0 0 15px ${themeColors.cardShadowSelected}`
      : `0 0 15px ${themeColors.cardShadow}`;
  };

  // Get hover shadow style
  const getHoverShadowStyle = () => {
    return `0 0 20px ${themeColors.cardShadowSelected}`;
  };

  // Get border color based on selection state
  const getBorderColor = () => {
    return isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder;
  };

  return (
    <motion.div
      className="lp-relative"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="lp-group lp-relative lp-overflow-hidden lp-backdrop-blur-sm
          lp-border lp-rounded-2xl lp-cursor-pointer
          lp-transition-all lp-duration-700 lp-h-full lp-flex lp-flex-col"
        style={{
          backgroundColor: themeColors.cardBackground,
          borderColor: getBorderColor(),
          boxShadow: getShadowStyle()
        }}
        onClick={() => onToggle(category)}
        whileHover={{
          scale: 1.02,
          boxShadow: getHoverShadowStyle()
        }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Image Section - Only shown if showImages is true */}
        {showImages && (
          <div className="lp-relative lp-h-48 lp-overflow-hidden">
            <ImageWithPlaceholder
              image={category?.image}
              alt={category.name}
              className="lp-w-full lp-h-full lp-object-cover"
              placeholderColors={{
                from: themeColors.placeholderGradientFrom,
                to: themeColors.placeholderGradientTo,
                iconBackground: themeColors.placeholderIconBackground
              }}
            />

            {/* Overlay Gradient - On top of image but under badges */}
            <div className="lp-absolute lp-inset-0 lp-z-[1] lp-pointer-events-none lp-bg-gradient-to-t lp-from-black/60 lp-via-black/20 lp-to-transparent group-hover:lp-from-black/70 group-hover:lp-via-black/30 lp-transition-all lp-duration-300" />

            {/* Selection Badge - Always on top */}
            <AnimatePresence>
              {isSelected && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="lp-absolute lp-top-4 lp-right-4 lp-w-8 lp-h-8 lp-rounded-full lp-flex lp-items-center lp-justify-center lp-z-[2]"
                  style={{ backgroundColor: themeColors.selectionBackground }}
                >
                  <CheckCircle className="lp-w-5 lp-h-5" style={{ color: themeColors.selectionIcon }} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Content Section */}
        <div className="lp-p-6 lp-flex lp-flex-col lp-flex-1 lp-min-h-[142px] lp-relative">
          <div className="lp-flex lp-gap-2 lp-items-start">
            <Tag className="lp-w-5 lp-h-5 lp-shrink-0 lp-mt-1" style={{ color: themeColors.iconColor }} />
            <h3
              className="lp-text-xl lp-font-medium lp-mb-4 lp-overflow-hidden lp-text-ellipsis lp-flex-1"
              style={{
                color: themeColors.titleText,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {category.name}
            </h3>
          </div>

          {/* Only show descriptions if showDescriptions is true */}
          {showDescriptions && (
            <div className="lp-flex lp-flex-col lp-flex-1">
              <div className="lp-flex lp-gap-2 lp-items-start">
                <FileText className="lp-w-5 lp-h-5 lp-shrink-0 lp-mt-1" style={{ color: themeColors.iconColor }} />
                <p
                  className="lp-text-sm lp-leading-relaxed lp-overflow-hidden lp-text-ellipsis lp-mb-2 lp-flex-1"
                  style={{
                    color: themeColors.subtitleText,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {category.description}
                </p>
              </div>
              {shouldShowReadMore(category.description) && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReadMore(category.id);
                  }}
                  className="lp-text-sm lp-mt-auto lp-self-start lp-transition-colors lp-duration-300"
                  style={{ color: themeColors.buttonText }}
                  whileHover={{
                    scale: 1.05,
                    //@ts-expect-error
                    color: themeColors.buttonHoverText || themeColors.buttonText
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Read More
                </motion.button>
              )}
            </div>
          )}

          {/* If we're not showing images, show the selection indicator here */}
          {!showImages && isSelected && (
            <div className="lp-absolute lp-top-4 lp-right-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="lp-w-8 lp-h-8 lp-rounded-full lp-flex lp-items-center lp-justify-center"
                style={{ backgroundColor: themeColors.selectionBackground }}
              >
                <CheckCircle className="lp-w-5 lp-h-5" style={{ color: themeColors.selectionIcon }} />
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CategoryCard;
